import {
  ONBOARDING_HEARD_ABOUT_SOURCE_META,
  ONBOARDING_INGEST_CHOICE_META,
  type T_OnboardingHeardAboutSource,
  type T_OnboardingIngestChoice,
  type T_OnboardingOptionIcon,
} from "@lattice/core-lib/types/onboarding/onboarding.types";

export type T_OnboardingV2SourceOption = {
  icon: T_OnboardingOptionIcon;
  label: string;
  value: T_OnboardingHeardAboutSource;
};

export type T_OnboardingV2DetailOption = {
  choice: T_OnboardingIngestChoice;
  description: string;
  icon: T_OnboardingOptionIcon;
  label: string;
  recommended: boolean;
};

export const ONBOARDING_V2_SOURCE_OPTIONS = [
  {
    icon: ONBOARDING_HEARD_ABOUT_SOURCE_META.REDDIT.icon,
    label: "Reddit",
    value: "REDDIT",
  },
  {
    icon: ONBOARDING_HEARD_ABOUT_SOURCE_META.TWITTER_X.icon,
    label: "X",
    value: "TWITTER_X",
  },
  {
    icon: ONBOARDING_HEARD_ABOUT_SOURCE_META.INSTAGRAM.icon,
    label: "Instagram",
    value: "INSTAGRAM",
  },
  {
    icon: ONBOARDING_HEARD_ABOUT_SOURCE_META.TIKTOK.icon,
    label: "TikTok",
    value: "TIKTOK",
  },
  {
    icon: ONBOARDING_HEARD_ABOUT_SOURCE_META.YOUTUBE.icon,
    label: "YouTube",
    value: "YOUTUBE",
  },
  {
    icon: ONBOARDING_HEARD_ABOUT_SOURCE_META.WORD_OF_MOUTH.icon,
    label: "A friend",
    value: "WORD_OF_MOUTH",
  },
  {
    icon: ONBOARDING_HEARD_ABOUT_SOURCE_META.PODCAST.icon,
    label: "Podcast",
    value: "PODCAST",
  },
  {
    icon: ONBOARDING_HEARD_ABOUT_SOURCE_META.OTHER.icon,
    label: ONBOARDING_HEARD_ABOUT_SOURCE_META.OTHER.text,
    value: "OTHER",
  },
] as const satisfies readonly T_OnboardingV2SourceOption[];

export const ONBOARDING_V2_OTHER_SOURCE_TEXT_INPUT =
  ONBOARDING_HEARD_ABOUT_SOURCE_META.OTHER.text_input;

export const ONBOARDING_V2_DETAIL_OPTIONS = [
  {
    choice: "SNAPTRADE",
    description: "Securely link your account - fastest and most accurate.",
    icon: ONBOARDING_INGEST_CHOICE_META.SNAPTRADE.icon,
    label: "Connect your brokerage",
    recommended: true,
  },
  {
    choice: "PHOTO",
    description: "Take a photo or choose a screenshot of your holdings.",
    icon: ONBOARDING_INGEST_CHOICE_META.PHOTO.icon,
    label: "Upload a photo",
    recommended: false,
  },
  {
    choice: "FILE",
    description: "Import a PDF statement or CSV from your broker.",
    icon: ONBOARDING_INGEST_CHOICE_META.FILE.icon,
    label: "Upload a file",
    recommended: false,
  },
  {
    choice: "MANUAL",
    description: "Enter your positions through a simple form.",
    icon: ONBOARDING_INGEST_CHOICE_META.MANUAL.icon,
    label: "Add holdings manually",
    recommended: false,
  },
] as const satisfies readonly T_OnboardingV2DetailOption[];

export function getOnboardingV2Greeting(firstName: string | null): string {
  return `Hi${firstName ? ` ${firstName}` : ""}, I'm Prism. I'll tailor Lattice around how you invest.`;
}

export const ONBOARDING_V2_COPY = {
  completion:
    "You're set. We'll use this to tailor your portfolio home and Prism context.",
  detailContext:
    "To personalize Lattice, we need a snapshot of your portfolio. If you connect a brokerage, SnapTrade securely sends us read-only portfolio data, like what you hold, how much, and your cash balance. We can't trade, transfer, or withdraw your money, and we never see your brokerage login.",
  detailQuestion: "How would you like to add your holdings?",
  sourceIntroduction: "Let's get started. I have a few questions.",
  sourceQuestion: "How did you hear about us?",
  trustFootnote:
    "Lattice uses SnapTrade for read-only brokerage connections. You can disconnect anytime from settings.",
  trustIntroduction:
    "Connecting is the most accurate way to get value from Lattice - and it's built to protect you.",
  trustTitle: "Your account stays safe",
} as const;

export const ONBOARDING_V2_TRUST_POINTS = [
  {
    body: "We can see balances and holdings - never move, trade, or withdraw a cent.",
    title: "Read-only connection",
  },
  {
    body: "Your data is protected with 256-bit AES encryption, end to end.",
    title: "Bank-level encryption",
  },
  {
    body: "Credentials go straight to SnapTrade - the secure connection layer your brokerage uses.",
    title: "We never see your login",
  },
  {
    body: "Revoke access in one tap from settings whenever you want.",
    title: "Disconnect anytime",
  },
] as const;
